import * as React from 'react';
import { Address4, Address6 } from 'ip-address';
import FormModel from '../../../../utils/FormModel';
import PortOpeningRule from '../../../../models/entities/PortOpeningRule';
import { RuleAction } from '../../../../models/enums/POR/RuleAction';
import { RuleRegion } from '../../../../models/enums/POR/RuleRegion';
import { AbstractFormState, AbstractForm } from '../../../common/AbstractForm';
import { ValidationRule } from '../../../../utils/Validation';
import { RuleOperation } from '../../../../models/enums/POR/RuleOperation';
import { VirtualNetwork } from '../../../../models/entities/AzureResources/VirtualNetwork';

export interface RulesFormProps {
	visible: boolean;
	onConfirm: (rule: PortOpeningRule) => void;
	onCancel?: (event: React.ChangeEvent<any>) => void;
	onClose: (event: React.ChangeEvent<any>) => void;
	editingRule?: PortOpeningRule;
	validVirtualNetworks: VirtualNetwork[];
	isReadOnly?: boolean;
	isFromHistory?: boolean;
 }

export interface IRuleStateForm {
	operation: RuleOperation;
	action: RuleAction;
	region: RuleRegion;
	sourceIPs: string;
	destinationIPs: string;
	destinationPorts: string;
	description: string;
	isTemporary?: boolean;
	expirationDate?: Date;
	
}

	/** @abstract */
export abstract class AbstractRulesForm<
	Props extends RulesFormProps,
	State extends AbstractFormState<IRuleStateForm>
	> extends AbstractForm<Props, State> {

	componentDidUpdate(prevProps) {
		if (this.props.editingRule !== prevProps.editingRule) {
			if (this.props.editingRule != null) {
				this.setState({ stateForm: this.getRuleCopy() });
				this.IPsSelected.sourceIPs = this.props.editingRule.sourceIPs;
				this.IPsSelected.destinationIPs = this.props.editingRule.destinationIPs;
			} else {
				this.setState({ stateForm: this.getDefaultForm() });
				this.IPsSelected.sourceIPs = "";
				this.IPsSelected.destinationIPs = "";
			}
		}
	}

	parseMultipleValues = (values: string) => {
		return values?.split(/\n/).join(';')
			.split(' ').join('');
	};

	hasEmptyValues = (values: string): boolean => {
		return this.parseMultipleValues(values)?.split(';').some(value => {			
			return value.length === 0;
		});
	};

	isPrivateAddress = (ipAddress: Address4): boolean => {
		const privateSubnets = [
			'10.0.0.0/8',       // RFC1918 Class A
			'172.16.0.0/12',    // RFC1918 Class B
			'192.168.0.0/16',   // RFC1918 Class C
			'153.33.160.84/30',
			//'158.26.192.0/24',
			'158.26.194.0/24',
			'158.26.200.0/24',
			'158.26.201.0/24',
			/*
			'158.33.80.0/20',
			'158.33.96.0/20',
			'158.33.112.0/20',
			'158.33.128.0/20',
			'158.33.144.0/20',
			'158.33.160.0/20',
			'158.33.176.0/20',
			'158.33.192.0/20',
			'158.33.208.0/20',
			'158.33.224.0/20',
			'158.33.240.0/20',
			'158.34.120.0/24',
			'158.34.122.0/24',
			'158.34.128.0/21',
			'158.34.136.0/24',
			'158.34.137.0/24',
			'158.34.138.0/24',
			'158.34.139.0/24',
			'158.34.140.0/24',
			'158.34.142.0/24',
			'158.34.143.0/24',
			*/
			'183.58.7.60/30',
			//'210.13.96.240/29',
			'210.13.102.224/27',
			'219.131.67.140/30',

			//IPs for Exodus
			'158.26.192.0/19',
			'158.33.0.0/16',
			'158.34.0.0/16',
			'210.13.96.0/19'
		];

		return privateSubnets.some(subnet => ipAddress.isInSubnet(new Address4(subnet)));
	};

	isIpAddressInputValid = (input: string, onlyPrivate: boolean): boolean => {
		try {
			if (input.includes('-')) {
				const [startRange, endRange] = input.split('-');
				const startAddress = new Address4(startRange);
				const endAddress = new Address4(endRange);

				return (startAddress.toHex() < endAddress.toHex()) &&
					(!onlyPrivate ||
						(this.isPrivateAddress(startAddress) && this.isPrivateAddress(endAddress)));
			}
			const ip = new Address4(input);
			return ip.isCorrect() &&
					(
						!onlyPrivate ||
						(this.isPrivateAddress(ip) && !!onlyPrivate)
					);
			
		} catch {
			return false;
		}
	};

	isInternetIpAddressInputValid = (input: string): boolean => {
		let loopbackIP = new Address4('127.0.0.0/8');
		try {
			if (input.includes('-')) {
				const [startRange, endRange] = input.split('-');
				const startAddress = new Address4(startRange);
				const endAddress = new Address4(endRange);
				if (startAddress.isInSubnet(loopbackIP) || endAddress.isInSubnet(loopbackIP)) return false
				return (startAddress.toHex() < endAddress.toHex()) &&
					!(this.isPrivateAddress(startAddress) || this.isPrivateAddress(endAddress));
			}
			const ip = new Address4(input);
			return ip.isCorrect() && !this.isPrivateAddress(ip) && !ip.isInSubnet(loopbackIP);
		} catch {
			return false;
		}
	};


	// TODO: Move all these IPs validations, methods, etc.. to a separate/dedicated service
	isIpInAnyAddressSpace = (ipAddress: string, validAddressSpaces: string[]): boolean => {
		return !!validAddressSpaces?.length && validAddressSpaces.some(addressSpace => this.isIpInAddressSpace(ipAddress, addressSpace));
	}

	isIpInAddressSpace = (ipAddress: string, addressSpace: string): boolean => {
		try {
			var ip6 = new Address6(ipAddress);
			var addressSpace6 = new Address6(addressSpace);

			if (addressSpace6.isCorrect()) {
				return ip6.isInSubnet(addressSpace6);
			} else {
				var ip4 = new Address4(ipAddress);
				var addressSpace4 = new Address4(addressSpace);
				return ip4.isInSubnet(addressSpace4);
			}
		} catch (ex) {
			try {
				var ip4 = new Address4(ipAddress);
				var addressSpace4 = new Address4(addressSpace);
				return ip4.isInSubnet(addressSpace4);
			}
			catch (ex) {
				return false
			}
		}
	}

	isPortInputValid = (value: string, minimumPort: number): boolean => {
		const ValidPort = (port) => {
			if(port.includes(",")) return -1;
			const maxmimumPort = 65535;
			const parsedPort = parseInt(port);

			if (!isNaN(parsedPort) && parsedPort >= minimumPort && parsedPort <= maxmimumPort)
				return parsedPort;
			return -1;
		};
		
		if (/^([0-9])*([-])([0-9])*$/.test(value)) {
			const [startRange, endRange] = value.split('-').map(p => ValidPort(p));
			return (startRange > -1 && endRange > -1 && startRange < endRange);
		}
		return ValidPort(value) > -1;
	};

	isModalForNewRule() {
		return !this.props.isReadOnly && this.stateFormHandler().operation.value === RuleOperation.Add.value;
	}

	IPsSelected = {
		sourceIPs: "",
		destinationIPs: ""
	}
	addOrDelIPSelected = (target: string, substring: string, addAction: boolean): string => {
		let list = [];
		let total = this.IPsSelected[target];
		if (total != "")
			list = total.split(";");
		if (addAction) {
			list.push(substring)
		} else {
			const index = list.indexOf(substring);
			if (index > -1) {
				list.splice(index,1)
			}
		}
		let result = list.join(";");
		this.IPsSelected[target] = result;
		return result
	}
	getStateFormHandlers() {
		return {
			operation: {
				value: this.state.stateForm.operation?.value,
				validation: this.formModel.fields.operation.validation,
				onChange: (event) => {
					this.handleStateFormChange('operation', new RuleOperation().fromValue(event.target.value));
				},
				options: (this.state.stateForm.operation?.value === RuleOperation.Add.value) ?
					new RuleOperation().list().sort((a, b) => a.name > b.name ? 1 : -1) : [
						RuleOperation.Modify,
						RuleOperation.Remove
					],
				disabled: this.state.stateForm.operation?.value === RuleOperation.Add.value
			},
			action: {
				label: 'Action',				
				value: this.state.stateForm.action?.value,
				validation: this.formModel.fields.action.validation,
				disabled: this.state.stateForm.action,
				onChange: (event) => {
					this.handleStateFormChange('action', new RuleAction().fromValue(event.target.value));
				}
			},
			isTemporary: {
				label: 'Type',				
				value: this.state.stateForm.isTemporary,
				validation: this.formModel.fields.isTemporary?.validation,
				disabled: this.state.stateForm.isTemporary,
				onChange: (event) => {
					this.handleStateFormChange('isTemporary', event.target.value);
				},
				note: this.state.stateForm.isTemporary?.toString()=='true' ? 'Temporary rule will be deleted in 30 days' : ''
			},
			expirationDate: {
				
				label: 'Expiration Date',
				value: this.state.stateForm.expirationDate?.toString(),
				disabled: true
			},
			region: {
				label: 'Region',
				value: this.state.stateForm.region?.value,
				className: 'full-width',
				onChange: (event) => this.handleStateFormChange('region', new RuleRegion().fromValue(event.target.value)),
				validation: this.formModel.fields.region.validation
			},
			sourceIPs: {
				label: 'Source (IP)',
				value: this.state.stateForm.sourceIPs,
				validation: this.formModel.fields.sourceIPs.validation,
				onChange: (event) => {
					if (event.target.checked == undefined) {
						this.handleStateFormChange('sourceIPs', event.target.value);
						console.log("event.target.value: ", event.target.value)
					} else{
						this.handleStateFormChange('sourceIPs', this.addOrDelIPSelected('sourceIPs', event.target.value, event.target.checked));
						console.log("event.target.value: ", event.target.value, " event.target.checked:", event.target.checked);
					}
				},
				onFocus: (event) => {
					console.log("focus");
					for (let key in event.target)
						console.log(key + ":" + event.target[key]);
				},
				note: 'Multiple values are allowed, separated by semicolons.'
			},
			destinationIPs: {
				label: 'Destination IP',
				value: this.state.stateForm.destinationIPs,
				validation: this.formModel.fields.destinationIPs.validation,
				onChange: (event) => {
					if (event.target.checked == undefined)
						this.handleStateFormChange('destinationIPs', event.target.value);
					else
						this.handleStateFormChange('destinationIPs', this.addOrDelIPSelected('destinationIPs', event.target.value, event.target.checked));
				},
				note: 'Multiple values are allowed, separated by semicolons.'
			},
			destinationPorts: {
				label: 'Destination Port(s)',
				value: this.state.stateForm.destinationPorts,
				validation: this.formModel.fields.destinationPorts.validation,
				onChange: (event) => {
					this.handleStateFormChange('destinationPorts', event.target.value);
				},
				note: 'Multiple values are allowed, separated by semicolons. Port ranges are accepted.'
			},
			description: {
				label: 'Description',
				value: this.state.stateForm.description,
				validation: this.formModel.fields.description.validation,
				onChange: (event) => {
					if (event.target.value.length <= 140) {
						this.handleStateFormChange('description', event.target.value);
					}
				},
				note: `${this.state.stateForm.description.length} / 140` 
			}
		};
	}
	
	getDefaultForm() {
		return {
			isTemporary: null,
			expirationDate: null,
			operation: null,
			action: null,
			region: null,
			sourceIPs: '',
			destinationIPs: '',
			destinationPorts: '',
			description: '',
			...this.getDefaultFormState()
		};
	}

	abstract getDefaultFormState();

	getRuleCopy() {
		return { ...this.props.editingRule };
	}

	handleCancel = (e) => {
		this.resetState();
		this.props.onClose(e);
		this.IPsSelected.sourceIPs = "";
		this.IPsSelected.destinationIPs = "";
	};

	handleFormChange = (event, field: string) => {
		const stateForm = { ...this.state.stateForm };
		stateForm[field] = event.target.value;
		this.setState({ stateForm });
	};

	handleRequest = async (e) => {
		this.props.onClose(e);
		const model = this.formModel.create(PortOpeningRule);
		this.props.onConfirm(model);
		this.resetState();
		this.IPsSelected.sourceIPs = "";
		this.IPsSelected.destinationIPs = "";
	};

	formCanBeEdited() {
		return !this.props.isReadOnly && this.state.stateForm.operation?.name !== RuleOperation.Remove.name;
	}

	handleStateFormChange(field: string, value: any): void;
	handleStateFormChange(field: string[], value: any[]): void;

	handleStateFormChange(field, value): void {
		const fields = [].concat(...[field]);
		const values = !Array.isArray(field) ? [value] : value;
		const form = this.state.stateForm;
		fields.forEach((fieldName, index) => {
			form[fieldName] = values[index];
		});
		this.setState({ stateForm: form });
	}

	initFormModel() {
		this.formModel = new FormModel(
			{
				isTemporary: {
					getValue: () => this.state.stateForm.isTemporary,
					validation: {
						required: true,						
					},
				},
				 expirationDate: {
        			getValue: () => { this.state.stateForm.expirationDate?.toString();
					},
				},
				operation: {
					getValue: () => this.state.stateForm.operation,
					validation: {
						required: true,
					},
				},
				action: {
					getValue: () => this.state.stateForm.action,
					validation: {
						required: true,
					},
				},
				region: {
					getValue: () => this.state.stateForm.region,
					validation: {
						required: true,
					},
				},
				sourceIPs: {
					getValue: () => this.parseMultipleValues(this.state.stateForm.sourceIPs),
					validation: {
						required: true,
						rules: [
							{
								assert: () => !this.hasEmptyValues(this.state.stateForm.sourceIPs),
								message: 'Empty values are not allowed.'
							}
						]
					},
				},
				destinationIPs: {
					getValue: () => this.parseMultipleValues(this.state.stateForm.destinationIPs),
					validation: {
						required: true,
						rules: [
							{
								assert: () => !this.hasEmptyValues(this.state.stateForm.destinationIPs),
								message: 'Empty values are not allowed.'
							}
						]
					},
				},
				destinationPorts: {
					getValue: () => this.parseMultipleValues(this.state.stateForm.destinationPorts),
					validation: {
						required: true,
						rules: [
							{
								assert: () => !this.hasEmptyValues(this.state.stateForm.destinationPorts),
								message: 'Empty values are not allowed.'
							}
						]
					},
				},
				description: {
					getValue: () => this.state.stateForm.description,
					validation: {
						required: true,
						rules: [
							{
								assert: () => this.state.stateForm.description?.trim().length >= 5,
								message: 'Description must have at least 5 characters.',
							}
						],
					},
				}
			}
		);
	}

	PortInputValidationRule = ({ minimumPort }): ValidationRule => ({
		assert: (value) => {
			const ports = this.parseMultipleValues(value).split(';');
			return ports.every(port => this.isPortInputValid(port, minimumPort));
		},
		message: (value) => {
			const invalidPort = this.parseMultipleValues(value).split(';').find(port => !this.isPortInputValid(port, minimumPort));
			return `${invalidPort} is an invalid port input.`;
		}
	});

	IpInputValidationRule = ({ onlyPrivateAddresses }): ValidationRule => ({
		assert: (value) => {
			const addresses = this.parseMultipleValues(value).split(';');
			return addresses.every(address => this.isIpAddressInputValid(address, onlyPrivateAddresses));
		},
		message: (value) => {
			const invalidIp = this.parseMultipleValues(value).split(';').find(address => !this.isIpAddressInputValid(address, onlyPrivateAddresses));
			return `${invalidIp} is an invalid address.`;
		}
	});

	//Added by Kenny to check Internet IPs
	InternetIpInputValidationRule = (): ValidationRule => ({
		assert: (value) => {
			const addresses = this.parseMultipleValues(value).split(';');
			return addresses.every(address => this.isInternetIpAddressInputValid(address));
		},
		message: (value) => {
			const invalidIp = this.parseMultipleValues(value).split(';').find(address => !this.isInternetIpAddressInputValid(address));
			return `${invalidIp} is an invalid address.`;
		}
	});

	IpIsInAddressSpacesRule = (): ValidationRule => ({
		assert: (sourceIpsInput) => {
			const sourceIps = this.parseMultipleValues(sourceIpsInput).split(';');
			const subnets = this.props.validVirtualNetworks.flatMap(vNet => vNet.properties.subnets);
			const addressSpaces = subnets.flatMap(subnet => subnet.properties.addressPrefix ?? subnet.properties.addressPrefixes);
			return sourceIps.every(ip => this.isIpInAnyAddressSpace(ip, addressSpaces));
		},
		message: (sourceIpsInput) => {
			const subnets = this.props.validVirtualNetworks.flatMap(vNet => vNet.properties.subnets);
			const addressSpaces = subnets.flatMap(subnet => subnet.properties.addressPrefix ?? subnet.properties.addressPrefixes);
			const invalidIp = this.parseMultipleValues(sourceIpsInput).split(';').find(ip => !this.isIpInAnyAddressSpace(ip, addressSpaces));
			return `${invalidIp} is not part of any valid address space.`;
		}
	});

	resetState = () => {
    	this.setState({ stateForm: this.getDefaultForm() });
	};
	
	stateFormHandler = () => this.getStateFormHandlers();
}